import { useState, useEffect, useCallback, useRef } from 'react'
import clsx from 'clsx'
import BigNumber from 'bignumber.js'
import './PercentageSlider.css'

export default function PercentageSlider({
  value,
  dotAmount = 5,
  customScaleLabelList = [],
  labelPostfix = '%',
  showLabel = false,
  showValueLabelOnLeft = false,
  showValueLabelOnDrag = false,
  roundToScale = false,
  onUpdate = () => {}
}) {
  const indicatorContainerRef = useRef(null)
  const dotIndicatorRef = useRef(null)
  const progressIndicatorRef = useRef(null)
  const offsetRef = useRef(0)
  const coordinateRef = useRef({ x: 0, offset: 0, isMove: false })
  const [hoverLabelIndex, setHoverLabelIndex] = useState(-1)

  const setElementsOffset = offset => {
    dotIndicatorRef.current.style.transform = `translateX(${offset}px)`
    progressIndicatorRef.current.style.transform = `translateX(${offset}px)`
  }

  const handleMouseMove = useCallback(
    e => {
      if (
        !roundToScale &&
        coordinateRef.current.isMove &&
        dotIndicatorRef.current
      ) {
        const maxOffset = coordinateRef.current.maxOffset
        let offset = e.clientX - coordinateRef.current.x + offsetRef.current
        offset = Math.min(offset, maxOffset)
        offset = Math.max(offset, 0)
        coordinateRef.current.offset = offset
        setElementsOffset(offset)
        onUpdate(Math.floor((offset / maxOffset) * 100))
      }
    },
    [roundToScale, onUpdate]
  )

  const handleMouseUp = useCallback(
    e => {
      if (!coordinateRef.current.isMove) {
        return
      }
      offsetRef.current = coordinateRef.current.offset
      coordinateRef.current = {
        x: coordinateRef.current.x,
        offset: 0,
        isMove: false
      }
      document.removeEventListener('mousemove', handleMouseMove)
    },
    [handleMouseMove]
  )

  const handleMouseDown = useCallback(
    e => {
      const containerWidth = indicatorContainerRef.current.offsetWidth
      coordinateRef.current = {
        x: e.clientX,
        offset: coordinateRef.current.offset,
        maxOffset: containerWidth - dotIndicatorRef.current.offsetWidth / 2,
        isMove: true
      }
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp, { once: true })
    },
    [handleMouseMove, handleMouseUp]
  )

  const handleClickDot = (percentage, index) => {
    const containerWidth = indicatorContainerRef.current.offsetWidth
    const maxOffset = containerWidth - dotIndicatorRef.current.offsetWidth / 2
    const offset = (maxOffset * percentage) / 100
    offsetRef.current = offset
    setElementsOffset(offset)
    onUpdate(percentage, index)
  }

  const handleClickPercentageSlider = e => {
    const rect = indicatorContainerRef.current.getBoundingClientRect()
    const offset = e.clientX - rect.left
    const containerWidth = indicatorContainerRef.current.offsetWidth
    const maxOffset = containerWidth - dotIndicatorRef.current.offsetWidth / 2
    if (roundToScale) {
      return
    }
    if (offsetRef.current !== offset) {
      offsetRef.current = offset
      setElementsOffset(offset)
      onUpdate(
        Math.max(Math.min(Math.floor((offset / maxOffset) * 100), 100), 0)
      )
    }
  }

  useEffect(() => {
    if (value === '' || coordinateRef.current.isMove) {
      return
    }
    const containerWidth = indicatorContainerRef.current.offsetWidth
    const maxOffset = containerWidth - dotIndicatorRef.current.offsetWidth / 2
    let offset = (value / 100) * maxOffset
    offset = Math.min(offset, maxOffset)
    offset = Math.max(offset, 0)
    offsetRef.current = offset
    setElementsOffset(offset)
  }, [value])

  const dotBasePercentage = BigNumber(100)
    .div(BigNumber(dotAmount).minus(1))
    .toNumber()
  const hasCustomScale = customScaleLabelList.length > 0
  const DotList = Array.from({ length: dotAmount }).map((_, i) => {
    const dotValue = BigNumber(dotBasePercentage)
      .times(i)
      .toFixed(0, BigNumber.ROUND_DOWN)
    const dotLabel = hasCustomScale ? customScaleLabelList[i] : dotValue
    return (
      <div
        key={dotValue}
        className={clsx('dot', {
          active: BigNumber(dotValue).lte(value),
          highlight: hoverLabelIndex === i
        })}
        onClick={() => handleClickDot(dotValue, i)}
      >
        {showLabel && (
          <div
            className="dot-value-label"
            onClick={e => {
              e.stopPropagation()
              handleClickDot(dotValue, i)
            }}
            onMouseEnter={() => setHoverLabelIndex(i)}
            onMouseLeave={() => setHoverLabelIndex(-1)}
          >
            {dotLabel}
            {labelPostfix}
          </div>
        )}
      </div>
    )
  })
  return (
    <div className="percentage-slider-container">
      {showValueLabelOnLeft && (
        <div className="percentage-label">
          {BigNumber(value).toFixed(0, BigNumber.ROUND_DOWN)}%
        </div>
      )}
      <div
        className={clsx('percentage-slider', { disabled: roundToScale })}
        onClick={handleClickPercentageSlider}
      >
        <div className="percentage-line">
          <div
            ref={progressIndicatorRef}
            className="percentage-line-progress"
          />
        </div>
        <div ref={indicatorContainerRef} className="dot-container">
          {DotList}
          <div
            ref={dotIndicatorRef}
            className="percentage-dot-indicator noselect"
            onMouseDown={handleMouseDown}
          >
            {showValueLabelOnDrag && (
              <div className="percentage-tooltip">
                {BigNumber(value).toFixed(0, BigNumber.ROUND_DOWN)}%
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
